/**
 * Scripture-first response types — DB-driven, no LLM inference.
 *
 * Used when the chat pipeline detects a verse_lookup intent.
 * Every field comes from SQLite or ChromaDB — instant response.
 */
import { z } from 'zod';
import { CrossRefSchema } from './rich-response.js';
import { PericopeSchema } from './chat-response.js';
/** Strong's definition preloaded server-side for a word */
export const WordDefinitionSchema = z.object({
    id: z.string(),
    language: z.enum(['hebrew', 'greek']),
    native: z.string(),
    phonetic: z.string(),
    definition: z.string(),
});
/** A single word in a verse with Strong's mapping */
export const ScriptureWordSchema = z.object({
    word: z.string(),
    precedingPunctuation: z.string().default(''),
    succeedingPunctuation: z.string().default(''),
    strongsNumbers: z.array(z.string()).default([]),
    isJesusWord: z.boolean().default(false),
    definition: WordDefinitionSchema.optional(),
    websterDefinition: z.string().optional(),
});
/** A verse with word-level data for interactive rendering */
export const ScriptureVerseSchema = z.object({
    reference: z.string(),
    testament: z.number(),
    book: z.number(),
    chapter: z.number(),
    verse: z.number(),
    text: z.string(),
    words: z.array(ScriptureWordSchema),
});
/** Textual variant between KJV (TR) and NASB (WH) manuscripts */
export const TextualVariantSchema = z.object({
    reference: z.string(),
    category: z.enum([
        'word_substitution', 'addition', 'omission',
        'major_addition', 'full_verse_omission',
    ]),
    theologicalImpact: z.enum(['low', 'medium', 'high']),
    strongsOnlyInKJV: z.array(z.string()),
    strongsOnlyInNASB: z.array(z.string()),
    affectedConcepts: z.array(z.string()),
});
/** Commentary generation state */
export const CommentaryStateSchema = z.enum([
    'not-requested', 'loading', 'complete', 'error',
]);
/** Complete scripture-first response — no LLM, pure DB */
export const ScriptureResponseSchema = z.object({
    verses: z.array(ScriptureVerseSchema),
    crossRefs: z.array(CrossRefSchema).default([]),
    variants: z.array(TextualVariantSchema).default([]),
    pericope: PericopeSchema.nullable().optional(),
    commentaryState: CommentaryStateSchema.default('not-requested'),
    /** 'pericope' = whole-passage study: anchor is the pericope's first verse,
     *  renderers show In Context only (no per-verse analysis). Absent = 'verse'. */
    scope: z.enum(['verse', 'pericope']).optional(),
});
//# sourceMappingURL=scripture-response.js.map